import { Box, AspectRatio, Center, Progress } from '@vimeo/bokeh';

import { type ThumbnailProps } from './types';
import { OPACITY_TRANSITION } from '../../utils/constants';

const MIN_WIDTH = '8rem'; // 128px, when using in horizontal video card layout

const thumbnailGradient = {
  _after: {
    content: "''",
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    _groupHover: {
      background: `linear-gradient(180deg, rgba(14, 18, 22, 0) 0.5%, rgba(14, 18, 22, 0) 50%, rgba(14, 18, 22, 0.3) 100%)`,
    },
  },
};

/**
 * Thumbnail is a special image component
 * - Always maintains a fixed 16:9 aspect ratio and a black background to fill in the empty space.
 * - Supports a roaster of decoration components that will be displayed or hidden on hover.
 */
export const Thumbnail = ({
  src,
  srcSet,
  alt,
  loading,
  progress,
  centerDecoration,
  topLeftDecoration,
  topRightDecoration,
  bottomLeftDecoration,
  bottomRightDecoration,
  ...boxProps
}: ThumbnailProps) => {
  return (
    <Box
      position="relative"
      minW={MIN_WIDTH}
      aspectRatio="16 / 9"
      outline="0"
      borderRadius="md"
      overflow="hidden"
      backgroundColor="black"
      _focusWithin={{ '[data-component-id="decoration-wrapper"]': { opacity: 1 } }}
      data-group
      data-component-id="video-card-thumbnail"
      {...boxProps}
    >
      {/* Thumbnail */}
      <AspectRatio
        ratio={16 / 9}
        w="100%" // very likely no longer needed
        maxW="100%" // very likely no longer needed
        {...(!!bottomLeftDecoration && thumbnailGradient)}
      >
        <img
          alt={alt}
          src={src}
          srcSet={srcSet}
          loading={loading}
          style={{ objectFit: 'contain', userSelect: 'none' }}
        />
      </AspectRatio>

      {/* Decoration */}
      {centerDecoration ? (
        <Center position="absolute" inset="75">
          {centerDecoration}
        </Center>
      ) : null}

      {topLeftDecoration ? (
        <Box
          position="absolute"
          top="75"
          left="75"
          transition={OPACITY_TRANSITION}
          _groupHover={{ opacity: 0 }}
          _groupFocus={{ opacity: 0 }}
        >
          {topLeftDecoration}
        </Box>
      ) : null}

      {topRightDecoration ? (
        // do not manipulate the opacity of the top right decoration, some elements in this group needs to be always visible
        <Box position="absolute" top="75" right="75" transition={OPACITY_TRANSITION}>
          {topRightDecoration}
        </Box>
      ) : null}

      {bottomLeftDecoration ? (
        <Box position="absolute" bottom="75" left="75">
          {bottomLeftDecoration}
        </Box>
      ) : null}

      {bottomRightDecoration ? (
        <Box position="absolute" bottom="75" right="75">
          {bottomRightDecoration}
        </Box>
      ) : null}

      {progress && progress > 0 ? (
        <Progress
          aria-label={`${progress}% complete`}
          bottom="0"
          left="4px"
          position="absolute"
          size="xs"
          value={progress}
          width="calc(100% - 8px)"
          sx={{
            bgColor: 'gray.500',
            borderTopRadius: '0',
            borderBottomRadius: 'md',
          }}
        />
      ) : null}
    </Box>
  );
};
